import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Snackbar,
  Alert,
  Stack
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  List as ListIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../apiBase';
import { useAuth } from '../context/AuthContext';

export default function MultiHardcopySheet() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Dropdown Data
  const [appCodes, setAppCodes] = useState([]);
  const [vendorList, setVendorList] = useState([]);
  const [firmList, setFirmList] = useState([]);
  const [articleList, setArticleList] = useState([]);
  
  // Header State
  const [header, setHeader] = useState({
    userCode: '',
    billDate: '',
    vendorCode: '',
    firmCode: '',
    invoiceNumber: '',
    freightCharges: ''
  });

  // Rows State
  const [rows, setRows] = useState([
    {
      id: Date.now(),
      article: '',
      gstPct: '',
      qty: '',
      rate: '',
      billAmount: '',
      taxableAmount: '',
      cgst: '',
      sgst: '',
      igst: '',
      ewayBill: 'no'
    }
  ]);

  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const dateRef = useRef(null);

  // Fetch Data
  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token') || '';
        const headers = { Authorization: `Bearer ${token}` };

        const isSuper = user?.role === 'superadmin';

        const [resArticles, resVendors] = await Promise.all([
          fetch(`${API_BASE_URL}/api/articles`, { headers }).then(r => r.json()),
          fetch(`${API_BASE_URL}/api/vendors`, { headers }).then(r => r.json())
        ]);

        if (isSuper) {
          const resCodes = await fetch(`${API_BASE_URL}/api/user-application-codes`, { headers });
          const dataCodes = await resCodes.json().catch(() => ({}));
          const codes = Array.isArray(dataCodes.items)
            ? dataCodes.items.map((it) => it.code).filter(Boolean).sort()
            : [];
          setAppCodes(codes);
        } else {
          setAppCodes([]);
        }

        setArticleList(Array.isArray(resArticles.items) ? resArticles.items : []);
        
        const allVendors = Array.isArray(resVendors.items) ? resVendors.items : [];
        
        const vendors = allVendors.filter(i => String(i.firmVendor || '').trim().toLowerCase() === 'vendor');
        const firms = allVendors.filter(i => String(i.firmVendor || '').trim().toLowerCase() === 'firm');

        let uniqueVendors = Array.from(new Map(vendors.map(item => [String(item.firmVendorCode || '').trim(), item])).values());
        const uniqueFirms = Array.from(new Map(firms.map(item => [String(item.firmVendorCode || '').trim(), item])).values());

        const userAssociations = user?.association || [];
        if (!isSuper && user?.application_code) {
          setHeader(prev => ({ ...prev, userCode: user.application_code }));
        }

        if (!isSuper) {
          uniqueVendors = uniqueVendors.filter(v => userAssociations.includes(v.firmVendorCode));
          uniqueFirms = uniqueFirms.filter(f => userAssociations.includes(f.firmVendorCode));
        }

        uniqueVendors.sort((a, b) => String(a.firmVendorCode || '').localeCompare(String(b.firmVendorCode || '')));
        uniqueFirms.sort((a, b) => String(a.firmVendorCode || '').localeCompare(String(b.firmVendorCode || '')));

        setVendorList(uniqueVendors);
        setFirmList(uniqueFirms);
      } catch (err) {
        console.error('Error loading data', err);
      }
    };
    load();
  }, [user]);

  // Handlers
  const handleHeaderChange = (field) => (e) => {
    setHeader(prev => ({ ...prev, [field]: e.target.value }));
  };

  const addRow = () => {
    setRows(prev => [
      ...prev,
      {
        id: Date.now() + Math.random(),
        article: '',
        gstPct: '',
        qty: '',
        rate: '',
        billAmount: '',
        taxableAmount: '',
        cgst: '',
        sgst: '',
        igst: '',
        ewayBill: 'no'
      }
    ]);
  };

  const removeRow = (id) => {
    if (rows.length > 1) {
      setRows(prev => prev.filter(r => r.id !== id));
    }
  };

  const handleRowChange = (index, field) => (e) => {
    const value = e.target.value;
    setRows(prev => {
      const newRows = [...prev];
      const row = { ...newRows[index], [field]: value };

      // 1. Article Selection -> Auto GST
      if (field === 'article') {
        const selectedArticle = articleList.find(a => a.item === value);
        if (selectedArticle) {
          row.gstPct = String(selectedArticle.gstRatio || '');
        }
      }

      // 2. Calculation Logic
      // Recalculate taxable if billAmount or gstPct changes
      const bill = Number(row.billAmount) || 0;
      const gstVal = Number(row.gstPct) || 0;
      
      if (bill > 0) {
        // Formula: (Bill * 100) / (100 + GST)
        const denominator = 100 + gstVal;
        const calcTaxable = (bill * 100) / denominator;
        row.taxableAmount = calcTaxable.toFixed(2);
      } else if (field === 'billAmount') {
        row.taxableAmount = '';
      }

      // 3. Tax Calculation
      // Depends on header.vendorCode for Intra/Inter state check
      const vendor = vendorList.find(v => v.firmVendorCode === header.vendorCode);
      const isIntraState = vendor?.gstNumber?.startsWith('06'); // Haryana Code 06
      
      const taxableVal = Number(row.taxableAmount) || 0;
      const totalTax = taxableVal * (gstVal / 100);

      if (isIntraState) {
        const halfTax = totalTax / 2;
        row.cgst = halfTax > 0 ? halfTax.toFixed(2) : '';
        row.sgst = halfTax > 0 ? halfTax.toFixed(2) : '';
        row.igst = '';
      } else {
        row.cgst = '';
        row.sgst = '';
        row.igst = totalTax > 0 ? totalTax.toFixed(2) : '';
      }

      // 4. Rate Calculation
      const qty = Number(row.qty) || 0;
      if (qty > 0 && taxableVal > 0) {
        row.rate = (taxableVal / qty).toFixed(2);
      } else if (field === 'qty' || field === 'billAmount') {
        // If qty is cleared or bill amount cleared, clear rate
        // unless manually entered? Usually rate is derived.
        // If we want to support manual rate entry, we should only overwrite if inputs change.
        // But here we are deriving it strictly.
        // row.rate = ''; 
      }
      // Note: If user manually changes Rate, it might be overwritten by this logic if they touch other fields.
      // But typically Rate is derived.

      // 5. E-Way Bill
      // Logic: >= 50000 taxable -> Required (Yes)
      row.ewayBill = taxableVal >= 50000 ? 'yes' : 'no';

      newRows[index] = row;
      return newRows;
    });
  };

  // Re-run tax calculations if Vendor changes (Inter/Intra state)
  useEffect(() => {
    setRows(prev => prev.map(row => {
      const gstVal = Number(row.gstPct) || 0;
      const taxableVal = Number(row.taxableAmount) || 0;
      const totalTax = taxableVal * (gstVal / 100);
      
      const vendor = vendorList.find(v => v.firmVendorCode === header.vendorCode);
      const isIntraState = vendor?.gstNumber?.startsWith('06');

      const newRow = { ...row };
      if (isIntraState) {
        const halfTax = totalTax / 2;
        newRow.cgst = halfTax > 0 ? halfTax.toFixed(2) : '';
        newRow.sgst = halfTax > 0 ? halfTax.toFixed(2) : '';
        newRow.igst = '';
      } else {
        newRow.cgst = '';
        newRow.sgst = '';
        newRow.igst = totalTax > 0 ? totalTax.toFixed(2) : '';
      }
      return newRow;
    }));
  }, [header.vendorCode, vendorList]);

  const handleSubmit = async () => {
    if (!header.userCode || !header.billDate) {
      setToast({ open: true, message: 'User Code and Bill Date are required', severity: 'error' });
      return;
    }

    const payload = {
      items: rows.map(row => ({
        ...header,
        ...row,
        // Ensure numbers are numbers for the API
        billAmount: Number(row.billAmount) || 0,
        qty: Number(row.qty) || 0,
        gstPct: Number(row.gstPct) || 0,
        rate: Number(row.rate) || 0,
        freightCharges: Number(header.freightCharges) || 0,
        // Mapping 'yes'/'no' back to 'Required'/'Not Required' if backend prefers that?
        // Backend schema is text. 'Required'/'Not Required' was used in single entry.
        // Screenshot shows 'no'. I'll send what is in the state ('yes'/'no').
        // If consistency matters, I can map it. 
        // Single entry uses: isRequired ? 'Required' : 'Not Required'
        // Let's stick to the screenshot's 'no' unless it breaks reporting.
        // I will map 'yes' -> 'Required', 'no' -> 'Not Required' to match existing data?
        // Or just save 'yes'/'no'. Let's save 'yes'/'no' as it's a text field.
      }))
    };

    try {
      const token = localStorage.getItem('token') || '';
      const res = await fetch(`${API_BASE_URL}/api/multi-hardcopy-sheets/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      
      if (res.ok) {
        setToast({ 
          open: true, 
          message: `Saved Successfully! Imported: ${data.imported}, Skipped: ${data.skipped}`, 
          severity: 'success' 
        });
        // Reset rows but keep header? Or reset all?
        // Usually reset all for a new invoice.
        setHeader({
          userCode: '',
          billDate: '',
          vendorCode: '',
          firmCode: '',
          invoiceNumber: '',
          freightCharges: ''
        });
        setRows([{
          id: Date.now(),
          article: '',
          gstPct: '',
          qty: '',
          rate: '',
          billAmount: '',
          taxableAmount: '',
          cgst: '',
          sgst: '',
          igst: '',
          ewayBill: 'no'
        }]);
      } else {
        setToast({ open: true, message: data.error || 'Failed to save', severity: 'error' });
      }
    } catch (err) {
      setToast({ open: true, message: 'Network Error', severity: 'error' });
    }
  };

  return (
    <Layout title="Hardcopy Sheet Management">
      <Box sx={{ maxWidth: '100%', px: 2, pb: 4 }}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="text.secondary">
            Hardcopy Sheet Management
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, mt: 2 }}>
            <TextField
              label="Application User Code"
              select={user?.role === 'superadmin'}
              value={header.userCode}
              onChange={handleHeaderChange('userCode')}
              variant="standard"
              fullWidth
              InputProps={{ readOnly: user?.role !== 'superadmin' }}
            >
              {user?.role === 'superadmin' ? appCodes.map((code) => (
                <MenuItem key={code} value={code}>
                  {code}
                </MenuItem>
              )) : null}
            </TextField>
            
            <TextField
              label="Date of Bill Received"
              type="date"
              value={header.billDate}
              onChange={handleHeaderChange('billDate')}
              variant="standard"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            
            <TextField
              label="Vendor Code"
              select
              value={header.vendorCode}
              onChange={handleHeaderChange('vendorCode')}
              variant="standard"
              fullWidth
            >
              {vendorList.map(v => <MenuItem key={v.id} value={v.firmVendorCode}>{v.firmVendorCode}</MenuItem>)}
            </TextField>
            
            <TextField
              label="Firm Code"
              select
              value={header.firmCode}
              onChange={handleHeaderChange('firmCode')}
              variant="standard"
              fullWidth
            >
              {firmList.map(f => <MenuItem key={f.id} value={f.firmVendorCode}>{f.firmVendorCode}</MenuItem>)}
            </TextField>
            
            <TextField
              label="Freight & Forwarding Charges / Loading & Unloading"
              value={header.freightCharges}
              onChange={handleHeaderChange('freightCharges')}
              variant="standard"
              fullWidth
            />
            
            <TextField
              label="Invoice Number"
              value={header.invoiceNumber}
              onChange={handleHeaderChange('invoiceNumber')}
              variant="standard"
              fullWidth
            />
          </Box>
        </Paper>

        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" color="primary.main" sx={{ fontWeight: 'bold' }}>
            Article Details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Add as many articles as required for this invoice. Each line will be saved separately.
          </Typography>
        </Box>

        {rows.map((row, index) => (
          <Paper 
            key={row.id}
            sx={{ 
              p: 3, 
              mb: 3, 
              borderLeft: '5px solid #8e24aa', // Purple border as requested
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
            }}
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(12, 1fr)' }, gap: 3 }}>
              {/* Row 1 */}
              <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 4' } }}>
                <TextField
                  label="Article"
                  select
                  value={row.article}
                  onChange={handleRowChange(index, 'article')}
                  variant="standard"
                  fullWidth
                >
                  {articleList.map(a => <MenuItem key={a.id} value={a.item}>{a.item}</MenuItem>)}
                </TextField>
              </Box>
              <Box sx={{ gridColumn: { xs: 'span 6', md: 'span 2' } }}>
                <TextField
                  label="Applicable GST %"
                  value={row.gstPct}
                  onChange={handleRowChange(index, 'gstPct')}
                  variant="standard"
                  fullWidth
                />
              </Box>
              <Box sx={{ gridColumn: { xs: 'span 6', md: 'span 2' } }}>
                <TextField
                  label="Qty"
                  value={row.qty}
                  onChange={handleRowChange(index, 'qty')}
                  variant="standard"
                  fullWidth
                />
              </Box>
              <Box sx={{ gridColumn: { xs: 'span 6', md: 'span 2' } }}>
                <TextField
                  label="Rate"
                  value={row.rate}
                  onChange={handleRowChange(index, 'rate')}
                  variant="standard"
                  fullWidth
                />
              </Box>
              <Box sx={{ gridColumn: { xs: 'span 6', md: 'span 2' } }}>
                <TextField
                  label="Bill Amount"
                  value={row.billAmount}
                  onChange={handleRowChange(index, 'billAmount')}
                  variant="standard"
                  fullWidth
                />
              </Box>

              {/* Row 2 */}
              <Box sx={{ gridColumn: { xs: 'span 6', md: 'span 3' } }}>
                <TextField
                  label="Taxable Amount"
                  value={row.taxableAmount}
                  onChange={handleRowChange(index, 'taxableAmount')}
                  variant="standard"
                  fullWidth
                />
              </Box>
              <Box sx={{ gridColumn: { xs: 'span 6', md: 'span 3' } }}>
                <TextField
                  label="CGST"
                  value={row.cgst}
                  variant="standard"
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Box>
              <Box sx={{ gridColumn: { xs: 'span 6', md: 'span 3' } }}>
                <TextField
                  label="SGST"
                  value={row.sgst}
                  variant="standard"
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Box>
              <Box sx={{ gridColumn: { xs: 'span 6', md: 'span 3' } }}>
                <TextField
                  label="IGST"
                  value={row.igst}
                  variant="standard"
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Box>

              {/* Row 3 */}
              <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 4' } }}>
                <TextField
                  label="E-Way Bill (Auto)"
                  value={row.ewayBill}
                  variant="standard"
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Box>
            </Box>

            {/* Remove Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button 
                color="error" 
                startIcon={<DeleteIcon />} 
                onClick={() => removeRow(row.id)}
                sx={{ textTransform: 'uppercase' }}
              >
                REMOVE ARTICLE
              </Button>
            </Box>
          </Paper>
        ))}

        <Stack direction="column" alignItems="center" spacing={2} sx={{ mt: 3 }}>
          <Button 
            variant="contained" 
            color="secondary" 
            startIcon={<AddIcon />} 
            onClick={addRow}
            sx={{ bgcolor: '#9c27b0', '&:hover': { bgcolor: '#7b1fa2' } }}
          >
            ADD ANOTHER ARTICLE
          </Button>
          
          <Button 
            variant="contained" 
            color="secondary" 
            onClick={handleSubmit}
            sx={{ bgcolor: '#7b1fa2', px: 5 }}
          >
            SUBMIT &gt;
          </Button>
          
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<ListIcon />}
            onClick={() => navigate('/hardcopy-sheet')}
            sx={{ mt: 2 }}
          >
            VIEW HARDCOPY SHEET LIST
          </Button>
        </Stack>

        <Snackbar
          open={toast.open}
          autoHideDuration={3000}
          onClose={() => setToast({ ...toast, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
            {toast.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
}
